import React from 'react';

interface CardResultPillProps {
  /** Whether the rows on screen came from the all tables (ordersall/legsall). */
  includeClosed: boolean;
  /** Whether the backend capped the result set. */
  truncated?: boolean;
  /** The ceiling the backend applied, for the truncation wording. */
  limit?: number;
  /** How many custom-filter conditions are narrowing these rows (0 = none). */
  filterCount?: number;
  /** Human-readable "Field compare value" lines for the filter, for the hover. */
  filterSummary?: string;
}

/**
 * At-a-glance marker for anything non-default about a dashboard card's result set.
 *
 * Rendered into TanTable's `headerExtra` slot next to the card title, and null when
 * there is nothing to say — which is the common case, so an ordinary card's header is
 * untouched. It reports two independent things:
 *
 * * **`+ Closed`** — the card's filter opted into closed orders, so these rows come
 *   from ordersall/legsall. Individual closed rows are deliberately NOT marked: on an
 *   all-scope card most rows are closed, so per-row highlighting highlighted the norm
 *   (dropped after team review). This card-level statement carries it instead.
 * * **`capped`** — the backend hit DASHBOARD_ROW_LIMIT and rows are missing. This can
 *   happen in EITHER scope, so it is not gated on `+ Closed`.
 * * **`Custom Filtered · N`** — a custom filter is narrowing the rows. Named for the
 *   control that set it (V1's "Custom Filter"), and distinct from the column filters,
 *   which have their own amber chip: one re-queries the bucket, the other hides rows
 *   already fetched, and a user needs to know which one is costing them rows.
 *
 * Neutral grey for the scope, a warm accent for the cap: "includes closed orders" is a
 * statement of scope, while "capped" means data is genuinely missing.
 *
 * Scope and cap are driven by the RESPONSE rather than the card config, so they always
 * describe the rows on screen and never a filter edit that has not refetched.
 */
const CardResultPill: React.FC<CardResultPillProps> = ({
  includeClosed,
  truncated,
  limit,
  filterCount = 0,
  filterSummary,
}) => {
  if (!includeClosed && !truncated && filterCount <= 0) return null;

  const capNote = truncated
    ? `More than ${(limit ?? 0).toLocaleString()} rows matched, so only the first `
      + `${(limit ?? 0).toLocaleString()} are shown — narrow the filter to see the rest.`
    : '';
  const scopeNote = includeClosed
    ? 'Includes closed orders (ordersall / legsall).'
    : '';
  const title = [scopeNote, capNote].filter(Boolean).join(' ');
  const filtered = filterCount > 0;
  // Its own pill rather than another segment of the grey one: it is the only one of the
  // three the user put there themselves and can take back off, and it pairs with the
  // "Clear Custom Filters" button in the toolbar. Reading as a footnote on the scope
  // would bury the one state they can act on.
  const filterTitle = filterSummary
    ? `Showing only rows matching:\n${filterSummary}`
    : 'A custom filter is narrowing these rows.';

  return (
    <>
      {filtered && (
        <span
          title={filterTitle}
          className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-800 dark:border-indigo-500/60 dark:bg-indigo-500/20 dark:text-indigo-200"
        >
          Custom Filtered · {filterCount}
        </span>
      )}
      {(includeClosed || truncated) && (
        <span
          title={title}
          className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700 dark:border-slate-600 dark:bg-slate-700/50 dark:text-slate-200"
        >
          {includeClosed && <span>+ Closed</span>}
          {includeClosed && truncated && (
            <span className="font-bold text-amber-700 dark:text-amber-400" aria-hidden="true">·</span>
          )}
          {truncated && <span className="text-amber-700 dark:text-amber-400">capped</span>}
        </span>
      )}
    </>
  );
};

export default CardResultPill;
